import { API_BASE_URL, ENDPOINTS } from "@/config/api.enpoints";
import apiClient from "./apiClient";
import type { WebScrapingPayload } from "@/hooks/useAdminDashboardService";
import type { TenantProfilePayload } from "@/types/tenantService";

export type RequestQueryParams = {
  page: number;
  pageSize: number;
  search?: string;
  searchKeyword?: string;
  orderBy?: string;
  order?: "asc" | "desc";
  requestContext?: "UPGRADE" | "REGISTRATION";
  status?: string;
};

export const fetchRequestData = async ({
  page,
  pageSize,
  search,
  searchKeyword,
  orderBy,
  order,
  requestContext,
  status,
}: RequestQueryParams) => {
  try {
    const params = new URLSearchParams();
    params.set("page", page.toString());
    params.set("pageSize", pageSize.toString());
    if (search) params.set("search", search);
    if (!search && searchKeyword) params.set("searchKeyword", searchKeyword);
    if (orderBy) params.set("orderBy", orderBy);
    if (order) params.set("order", order);
    if (requestContext) params.set("requestContext", requestContext);
    if (status) params.set("status", status);
    const response = await apiClient.get(
      `${API_BASE_URL}${ENDPOINTS.ADMIN.request}?${params.toString()}`,
    );
    return response.data;
  } catch (error) {
    console.error("Failed to fetch request data:", error);
    throw error;
  }
};
export const getActiveTenants = async (params?: { searchKeyword?: string; pageSize?: number }) => {
  const response = await apiClient.get(
    `${API_BASE_URL}${ENDPOINTS.ADMIN.getActiveTenants}`,
    { params: { pageSize: 1000, ...params } }
  );
  return response.data;
};

export const getTenantsUsers = async () => {
  try {
    const response = await apiClient.get(
      `${API_BASE_URL}${ENDPOINTS.ADMIN.tenantsUsers}?pageSize=1000`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};
export const getTenantsUsedData = async (page: number, limit: number, searchKeyword?: string) => {
  const object: Record<string, string> = {
    page: page.toString(),
    pageSize: limit.toString(),
  };
  if (searchKeyword) {
    object.searchKeyword = searchKeyword;
  }
  const query = new URLSearchParams(object);
  try {
    const response = await apiClient.get(
      `${API_BASE_URL}${ENDPOINTS.ADMIN.tenantsUsedData}?${query}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};
export const tenantsUsageTracker = async (filters: {
  type: string;
  year?: number;
  fromDate?: string;
  toDate?: string;
}) => {
  try {
    const response = await apiClient.get(
      `${API_BASE_URL}${ENDPOINTS.ADMIN.tenantsUsageTracker}`,
      { params: filters },
    );
    return response.data;
  } catch (error) {
    console.error("Failed to fetch tenants usage:", error);
    throw error;
  }
};

export const tenantsUsageTrackerByid = async (params: {
  id: string;
  userId?: string;
  type: string;
  year?: number;
  fromDate?: string;
  toDate?: string;
}) => {
  try {
    const { id, ...queryParams } = params;
    const response = await apiClient.get(
      `${API_BASE_URL}${ENDPOINTS.ADMIN.tenantsUsageTrackerById(id)}`,
      { params: queryParams },
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const webScrapingAPI = async ({
  id,
  linkedin,
  website,
}: WebScrapingPayload) => {
  try {
    const response = await apiClient.post(
      `${API_BASE_URL}${ENDPOINTS.ADMIN.webScraping(id)}`,
      {
        linkedIn_url: linkedin,
        website_url: website,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Web Scraping API error:", error);
    throw error;
  }
};
export const tenantProfileUpdate = async (data: TenantProfilePayload) => {
  try {
    const response = await apiClient.post(
      `${API_BASE_URL}${ENDPOINTS.TENANT.tenantProfile}`,
      data,
    );
    return response.data;
  } catch (error) {
    console.error("Tenant Update Error:", error);
    throw error;
  }
};
export const tenantProfileUpdateById = async (data: TenantProfilePayload) => {
  try {
    const response = await apiClient.post(
      `${API_BASE_URL}${ENDPOINTS.TENANT.tenantProfileById}`,
      data,
    );
    return response.data;
  } catch (error) {
    console.error("Tenant Update Error:", error);
    throw error;
  }
};

export const tenantProfileCreate = async (
  id: string,
  data: TenantProfilePayload,
) => {
  try {
    const response = await apiClient.put(
      `${API_BASE_URL}${ENDPOINTS.TENANT.createTenantProfile(id)}`,
      data,
    );
    return response.data;
  } catch (error) {
    console.error("Tenant Update Error:", error);
    throw error;
  }
};

export const tenantProfileGet = async ({
  page = 1,
  pageSize = 10,
  tenantId = localStorage.getItem("tenantId"),
}) => {
  const objectData = {
    tenantId: tenantId ?? "",
    pageSize: pageSize.toString(),
    page: page.toString(),
  };
  const data = new URLSearchParams(objectData);
  try {
    const response = await apiClient.get(
      `${API_BASE_URL}${ENDPOINTS.TENANT.tenantProfile}?${data}`,
    );
    return response.data;
  } catch (error) {
    console.error("Tenant Update Error:", error);
    throw error;
  }
};
export const getTenantDashboardData = async () => {
  const response = await apiClient.get(
    `${API_BASE_URL}${ENDPOINTS.TENANT.getTenantDashboardData}`,
  );
  return response.data;
};
export const updateActiveTenantsById = async (
  payload: Record<string, string | boolean>,
  tenantId: string | null = localStorage.getItem("tenantId"),
) => {
  try {
    const updatedPayload = {
      ...payload,
      status:
        typeof payload.status === "boolean"
          ? String(payload.status)
          : payload.status,
    };

    const response = await apiClient.put(
      `${API_BASE_URL}${ENDPOINTS.ADMIN.updateActiveTenantsById(tenantId ?? "")}`,
      updatedPayload,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return response.data;
  } catch (error) {
    console.error("Tenant Update Error:", error);
    throw error;
  }
};

export const uploadTenantLogoAPI = async (id: string, formData: FormData) => {
  try {
    const response = await apiClient.put(
      `${API_BASE_URL}${ENDPOINTS.ADMIN.uploadTenantLogo(id)}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Tenant Logo Upload Error:", error);
    throw error;
  }
};

export const approveRequest = async (id: string) => {
  try {
    const response = await apiClient.post(
      `${API_BASE_URL}${ENDPOINTS.ADMIN.approveRequest(id)}`,
    );
    return response.data;
  } catch (error) {
    console.error("Failed to approve request:", error);
    throw error;
  }
};

export const rejectRequest = async (id: string) => {
  try {
    const response = await apiClient.post(
      `${API_BASE_URL}${ENDPOINTS.ADMIN.rejectRequest(id)}`,
    );
    return response.data;
  } catch (error) {
    console.error("Failed to reject request:", error);
    throw error;
  }
};

export type TenantDashboardOverviewParams = {
  page?: number;
  pageSize?: number;
  orderBy?: string;
};

export const getTenantDashboardOverview = async (
  params: TenantDashboardOverviewParams = {},
) => {
  const { data } = await apiClient.get(
    `${API_BASE_URL}${ENDPOINTS.TENANT.getTenantDashboardOverview}`,
    {
      params: {
        page: 1,
        pageSize: 1000,
        orderBy: "desc",
        ...params,
      },
    },
  );
  return data;
};


export const fetchAzureCostAPI = async () => {
  const { data } = await apiClient.get(`${API_BASE_URL}/v1/usage/azure-cost`);
  return data;
};
